import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, Star, Phone, Clock, Users2, Sparkles, Wine, ChefHat } from "lucide-react";
import { toast } from "sonner";
import PriceBlock from "@/components/PriceBlock";
import PreferenceCapture from "@/components/PreferenceCapture";

const CAT_LABEL = {
  starters: "Starters",
  mains: "Mains",
  sides: "Sides & breads",
  desserts: "Desserts",
  drinks_non_alc: "Soft drinks & mocktails",
  drinks_alc: "Wine, beer & cocktails",
};
const CAT_ORDER = ["starters", "mains", "sides", "desserts", "drinks_non_alc", "drinks_alc"];

// Resolve the per-head Tablo price for the given pax against a package's pax_tiers.
// Returns the top-level price if no tier matches.
function resolvePackagePrice(pkg, paxN) {
  if (!pkg) return { price: 0, list: 0, tier: null };
  const tiers = (pkg.pax_tiers || []).slice().sort((a, b) => (a.min_pax || 0) - (b.min_pax || 0));
  for (const t of tiers) {
    if (paxN >= (t.min_pax || 0) && paxN <= (t.max_pax || 0)) {
      return {
        price: t.price_per_head_inr,
        list: t.list_price_per_head_inr || t.price_per_head_inr,
        tier: t,
      };
    }
  }
  return {
    price: pkg.price_per_head_inr,
    list: pkg.list_price_per_head_inr || pkg.price_per_head_inr,
    tier: null,
  };
}

export default function RestaurantDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const packageId = params.get("package");
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [activeTab, setActiveTab] = useState("packages");
  const [pax, setPax] = useState(12);
  const [budget, setBudget] = useState(1500);
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [slot, setSlot] = useState("");
  const [loading, setLoading] = useState(false);
  const [floor, setFloor] = useState(null);
  const [capacity, setCapacity] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const brief = sessionStorage.getItem("tablo_brief");
        if (brief) {
          const b = JSON.parse(brief);
          if (b.pax) setPax(b.pax);
          if (b.budget) setBudget(b.budget);
          if (b.date) setDate(b.date);
          if (b.time) setSlot(b.time);
        }
        const { data } = await api.get(`/restaurants/${id}`);
        setData(data);
        const model = data.restaurant.dining_model;
        if (model === "a_la_carte") {
          // No package for a_la_carte — guest's budget is the constraint.
          setPkg(null);
          setActiveTab("menu");
          // Default slot from any package (slots are venue-wide, not package-specific in our seed)
          if (!slot && data.packages?.[0]?.available_slots?.length) setSlot(data.packages[0].available_slots[0]);
        } else {
          const initial = data.packages.find((p) => p.id === packageId) || data.packages[0];
          setPkg(initial);
          setActiveTab("packages");
          if (initial && !slot && initial.available_slots?.length) setSlot(initial.available_slots[0]);
        }
      } catch (e) {
        toast.error("Restaurant not found");
        nav("/home");
      }
    })();
  }, [id]); // eslint-disable-line

  // Recompute budget floor whenever pax changes — drives the feasibility hint.
  useEffect(() => {
    if (!id || !pax) return;
    (async () => {
      try {
        const { data: f } = await api.get(`/restaurants/${id}/budget-floor?pax=${pax}`);
        setFloor(f);
      } catch (_) {
        setFloor(null);
      }
    })();
  }, [id, pax]);

  // Check restaurant capacity whenever date/time/pax changes — tells the guest upfront
  // whether the venue still has room. Skips the call until slot is picked.
  useEffect(() => {
    if (!id || !date || !slot || !pax) { setCapacity(null); return; }
    (async () => {
      try {
        const { data: c } = await api.get(`/restaurants/${id}/capacity`, { params: { date, time: slot, pax } });
        setCapacity(c);
      } catch (_) {
        setCapacity(null);
      }
    })();
  }, [id, date, slot, pax]);

  const bookNow = async (mode = "package") => {
    setLoading(true);
    try {
      const brief = JSON.parse(sessionStorage.getItem("tablo_brief") || "{}");
      const usePackage = mode === "package" && pkg?.id;
      // If the user manually bumped budget on this page, prefer that over the brief.
      const effectiveBudget = budget && budget !== (brief.budget || 1500)
        ? budget
        : (brief.budget || budget || pkg?.price_per_head_inr || 1500);
      const res = await api.post("/bookings", {
        restaurant_id: id,
        package_id: usePackage ? pkg.id : null,
        pax,
        budget_per_head: effectiveBudget,
        date,
        time: slot,
        vibe: brief.vibe || pkg?.vibe_tag,
        purpose: brief.purpose || null,
        notes: brief.freeText || "",
        mode: brief.mode || "dining",
        guest_preferences: JSON.parse(sessionStorage.getItem("tablo_prefs") || "null"),
        billing: JSON.parse(sessionStorage.getItem("tablo_billing") || "null"),
      });
      if (mode === "curated" || mode === "pick") {
        nav(`/curated-menu/${res.data.id}?mode=${mode}`);
      } else if (usePackage && pkg.selection_rules && Object.keys(pkg.selection_rules).length > 0) {
        nav(`/package-selection/${res.data.id}`);
      } else {
        nav(`/checkout/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    if (!data?.menu_items) return {};
    return data.menu_items.reduce((acc, m) => {
      (acc[m.category] = acc[m.category] || []).push(m);
      return acc;
    }, {});
  }, [data]);

  if (!data) return <div className="py-10 text-light-ink animate-pulse">Loading...</div>;
  const r = data.restaurant;

  return (
    <div className="space-y-6 animate-fade-up pb-32">
      <div className="rounded-3xl overflow-hidden shadow-tablo">
        <img src={r.photos?.[0] || "https://images.pexels.com/photos/19447626/pexels-photo-19447626.jpeg"} alt={r.name} className="w-full aspect-[16/9] object-cover" data-testid="restaurant-photo" />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-serif text-3xl sm:text-4xl" data-testid="restaurant-name">{r.name}</h1>
            <span className="tablo-pill bg-amber-soft/30 text-ink text-[10px]" data-testid="restaurant-dining-model">
              {r.dining_model === "package_only" && "Chef curates"}
              {r.dining_model === "a_la_carte" && "À-la-carte"}
              {r.dining_model === "hybrid" && "Packages + Menu"}
            </span>
          </div>
          <p className="text-sm text-light-ink mt-1 flex items-center flex-wrap gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" strokeWidth={1.6}/> {r.area}</span>
            <span className="text-light-ink/40">•</span>
            <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber" strokeWidth={1.6}/> {r.google_rating}</span>
            <span className="text-light-ink/40">•</span>
            <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" strokeWidth={1.6}/> {r.contact_phone}</span>
          </p>
          <p className="text-light-ink text-sm mt-3 max-w-xl">{r.blurb}</p>
        </div>
      </div>

      {/* Preferences (allergens, spice, veg, drinks) — drives the curated menu */}
      {r.dining_model !== "package_only" && (
        <PreferenceCapture
          defaultAllergens={r.default_allergens || []}
          testid="restaurant-prefs"
        />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-cream border border-tablo-border">
          {r.dining_model !== "a_la_carte" && data.packages.length > 0 && (
            <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
          )}
          {r.dining_model !== "package_only" && (
            <TabsTrigger value="menu" data-testid="tab-menu">Full menu</TabsTrigger>
          )}
        </TabsList>
        {r.dining_model === "a_la_carte" && (
          <div className="mt-4 tablo-card p-5 border-amber-soft border-l-4" data-testid="alacarte-budget-callout">
            <p className="font-serif text-xl mb-1 flex items-center gap-2"><ChefHat className="w-5 h-5 text-amber" strokeWidth={1.6}/>Budget-based dining</p>
            <p className="text-sm text-light-ink mb-3">{r.name} doesn't run fixed packages. Tablo can curate a balanced menu for <span className="font-medium text-ink">{pax} guests</span> within your <span className="font-medium text-ink">₹{budget.toLocaleString("en-IN")}/head</span> budget — or you can design it yourself.</p>
            <div className="flex justify-between bg-cream rounded-xl p-3 text-sm">
              <div><span className="text-light-ink text-xs uppercase tracking-wider block">Group total</span><span className="font-serif text-xl">₹{(budget * pax).toLocaleString("en-IN")}</span></div>
              <div><span className="text-light-ink text-xs uppercase tracking-wider block">10% deposit</span><span className="font-serif text-xl">₹{Math.round(budget * pax * 0.10).toLocaleString("en-IN")}</span></div>
              <div><span className="text-light-ink text-xs uppercase tracking-wider block">Balance at venue</span><span className="font-serif text-xl">₹{(budget * pax - Math.round(budget * pax * 0.10)).toLocaleString("en-IN")}</span></div>
            </div>
          </div>
        )}
        <TabsContent value="packages" className="space-y-3 mt-4">
          {data.packages.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => setPkg(p)}
              className={`tablo-card p-5 w-full text-left transition-all ${pkg?.id === p.id ? "ring-2 ring-amber" : "hover:shadow-tablo-lg"}`}
              data-testid={`package-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-serif text-xl">{p.name}</p>
                  <p className="text-xs text-light-ink mt-1 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Users2 className="w-3 h-3" strokeWidth={1.6}/> {p.min_pax}–{p.max_pax} guests</span>
                    <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber" strokeWidth={1.6}/> {p.vibe_tag}</span>
                  </p>
                </div>
                {(() => {
                  const resolved = resolvePackagePrice(p, pax);
                  return (
                    <PriceBlock
                      listPrice={resolved.list}
                      tabloPrice={resolved.price}
                      suffix="/head"
                      size="md"
                      testid={`package-price-${p.id}`}
                    />
                  );
                })()}
              </div>
              {/* Pax-tier breakdown — visible when partner has defined tiers */}
              {p.pax_tiers && p.pax_tiers.length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`package-tiers-${p.id}`}>
                  {p.pax_tiers.map((t) => {
                    const active = pax >= t.min_pax && pax <= t.max_pax;
                    return (
                      <div
                        key={`${t.min_pax}-${t.max_pax}`}
                        className={`rounded-lg border px-2.5 py-1.5 text-[11px] ${active ? "border-amber bg-amber-soft/30" : "border-tablo-border bg-cream/70 text-light-ink"}`}
                      >
                        <p className="font-medium text-ink">{t.min_pax}–{t.max_pax} pax</p>
                        <p className="text-ink">₹{t.price_per_head_inr.toLocaleString("en-IN")}/head</p>
                        {active && <p className="text-amber text-[10px] mt-0.5">↑ your size</p>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {p.inclusions.map((inc) => <span key={inc} className="tablo-pill bg-cream border border-tablo-border text-ink">{inc}</span>)}
              </div>
              {p.selection_rules && Object.keys(p.selection_rules).length > 0 && (
                <div className="mt-3 pt-3 border-t border-tablo-border/60">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-amber font-medium mb-1.5">You'll pick</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-light-ink" data-testid={`package-rules-${p.id}`}>
                    {Object.entries(p.selection_rules).map(([cat, rule]) => (
                      <span key={cat}>
                        <span className="font-medium text-ink">
                          {typeof rule === "number" ? rule : (rule.total != null ? rule.total : `${rule.veg || 0}V+${rule.non_veg || 0}NV`)}
                        </span>{" "}{cat.replace("_", " ")}
                      </span>
                    ))}
                    <span className="text-light-ink/70">· chef sets portions for {p.min_pax}–{p.max_pax} guests</span>
                  </div>
                </div>
              )}
              {/* Dishes offered in this package — pulled from menu_item_ids */}
              {p.menu_item_ids && p.menu_item_ids.length > 0 && data.menu_items && (
                <div className="mt-3 pt-3 border-t border-tablo-border/60">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-amber font-medium mb-1.5">Dishes available in this package</p>
                  <div className="flex flex-wrap gap-1.5" data-testid={`package-dishes-${p.id}`}>
                    {data.menu_items.filter((m) => p.menu_item_ids.includes(m.id)).slice(0, 24).map((m) => (
                      <span key={m.id} className="text-[11px] px-2 py-0.5 rounded-full bg-cream border border-tablo-border text-ink inline-flex items-center gap-1">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${m.veg_flag ? "bg-green-500" : "bg-rose-500"}`}/>
                        {m.name}
                      </span>
                    ))}
                    {data.menu_items.filter((m) => p.menu_item_ids.includes(m.id)).length > 24 && (
                      <span className="text-[11px] text-light-ink">+ more</span>
                    )}
                  </div>
                </div>
              )}
            </button>
          ))}
        </TabsContent>
        <TabsContent value="menu" className="mt-4 space-y-6">
          {CAT_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-2">
                {cat === "drinks_alc" && <Wine className="w-4 h-4 text-amber" strokeWidth={1.6}/>}
                <p className="text-xs uppercase tracking-[0.18em] text-amber font-medium">{CAT_LABEL[cat]}</p>
              </div>
              <div className="tablo-card divide-y divide-tablo-border/60">
                {grouped[cat].map((m) => (
                  <div key={m.id} className="flex justify-between items-start p-4 gap-3" data-testid={`menu-item-${m.id}`}>
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <span className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${m.veg_flag ? "bg-green-600" : "bg-rose-600"}`}/>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium text-ink">{m.name}</p>
                          {m.must_try && (
                            <span className="text-[10px] uppercase tracking-wider bg-amber/30 text-ink px-1.5 py-0.5 rounded-full font-medium" data-testid={`musttry-${m.id}`}>★ Chef's pick</span>
                          )}
                          {m.spice_level > 0 && (
                            <span className="text-[10px]" title={`Spice ${m.spice_level}/3`}>{"🌶️".repeat(m.spice_level)}</span>
                          )}
                        </div>
                        {m.description && (
                          <p className="text-[11px] text-light-ink mt-0.5 line-clamp-2">{m.description}</p>
                        )}
                        {m.allergens?.length > 0 && (
                          <p className="text-[10px] text-rose-700/90 mt-0.5">Contains: {m.allergens.join(", ")}</p>
                        )}
                        {m.tags?.length > 0 && !m.description && (
                          <p className="text-[11px] text-light-ink uppercase tracking-wider mt-0.5">{m.tags.join(" · ")}</p>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      {m.list_price_inr > m.price_inr && (
                        <p className="text-[11px] text-light-ink/70 line-through leading-3">₹{m.list_price_inr}</p>
                      )}
                      <p className="text-sm text-ink font-medium">₹{m.price_inr}</p>
                      {m.list_price_inr > m.price_inr && (
                        <p className="text-[10px] text-amber font-medium uppercase tracking-wider">save ₹{m.savings_inr}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* Sticky bottom book panel — separate from inline content to avoid overlap */}
      <div className="fixed left-0 right-0 bottom-16 sm:bottom-20 z-30 px-5 pointer-events-none">
        <div className="max-w-3xl mx-auto tablo-card p-4 pointer-events-auto shadow-tablo-lg" data-testid="restaurant-book-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4 text-sm">
              <div><span className="text-[10px] uppercase text-light-ink block leading-3">Pax</span><span className="font-medium">{pax}</span></div>
              <div><span className="text-[10px] uppercase text-light-ink block leading-3">Date</span><span className="font-medium">{date}</span></div>
              <div>
                <span className="text-[10px] uppercase text-light-ink block leading-3">Slot</span>
                <span className="font-medium inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.6}/>{slot || "—"}</span>
              </div>
            </div>
            {pkg && r.dining_model !== "a_la_carte" && (activeTab === "packages" || r.dining_model === "package_only") && (() => {
              const resolved = resolvePackagePrice(pkg, pax);
              const total = resolved.price * pax;
              const deposit = Math.round(total * 0.10);
              return (
                <p className="text-xs text-light-ink" data-testid="restaurant-sticky-package-total">
                  ₹{resolved.price.toLocaleString("en-IN")}/head{resolved.tier ? <span className="text-amber"> (tier {resolved.tier.min_pax}-{resolved.tier.max_pax})</span> : null} · Total <span className="text-ink font-medium">₹{total.toLocaleString("en-IN")}</span> · 10% deposit <span className="text-ink font-medium">₹{deposit.toLocaleString("en-IN")}</span>
                </p>
              );
            })()}
            {(r.dining_model === "a_la_carte" || (r.dining_model === "hybrid" && activeTab === "menu")) && (
              <p className="text-xs text-light-ink">
                Budget <span className="text-ink font-medium">₹{(budget * pax).toLocaleString("en-IN")}</span> · deposit <span className="text-ink font-medium">₹{Math.round(budget * pax * 0.10).toLocaleString("en-IN")}</span>
              </p>
            )}
          </div>
          {/* Budget floor hint for à-la-carte modes */}
          {floor && (r.dining_model === "a_la_carte" || (r.dining_model === "hybrid" && activeTab === "menu")) && (
            <div className="mt-2.5">
              {budget < floor.min_decent_per_head_inr ? (
                <div className="text-xs bg-amber-soft/40 border border-amber/30 rounded-lg px-3 py-2 text-ink" data-testid="detail-floor-warning">
                  ⚠ ₹{budget}/head likely won't feed {pax} guests here. We recommend{" "}
                  <button onClick={() => setBudget(floor.recommended_per_head_inr)} className="font-medium underline" data-testid="detail-floor-bump-btn">
                    ₹{floor.recommended_per_head_inr}/head
                  </button>{" "}(≈ ₹{(floor.recommended_per_head_inr * pax).toLocaleString("en-IN")} total).
                </div>
              ) : (
                <p className="text-[11px] text-light-ink" data-testid="detail-floor-ok">
                  ✓ Decent budget for {pax} guests. Min for this venue ≈ ₹{floor.min_decent_per_head_inr}/head.
                </p>
              )}
            </div>
          )}
          {/* Capacity availability hint */}
          {capacity && (
            capacity.available ? (
              <p className="text-[11px] text-light-ink mt-2" data-testid="capacity-ok">
                ✓ Space available — {capacity.available_pax}/{capacity.max_concurrent_pax_dining} pax left in this slot.
              </p>
            ) : (
              <div className="mt-2 text-xs bg-rose-50 border border-rose-300 rounded-lg px-3 py-2 text-rose-900" data-testid="capacity-full">
                ⚠ This slot is full at {r.name} ({capacity.booked_pax}/{capacity.max_concurrent_pax_dining} pax already booked, {capacity.large_groups_in_slot}/{capacity.max_concurrent_large_groups} large groups). Pick another time or another venue.
              </div>
            )
          )}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(pkg?.available_slots || []).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSlot(s)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${slot === s ? "bg-ink text-cream border-ink" : "bg-cream text-ink border-tablo-border hover:border-amber/60"}`}
                data-testid={`slot-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            {(() => {
              const model = r.dining_model || (data.packages.length > 0 ? "hybrid" : "a_la_carte");
              // package_only → only Reserve
              if (model === "package_only") {
                return (
                  <Button onClick={() => bookNow("package")} disabled={loading || !slot || !pkg} className="flex-1 tablo-btn-primary text-sm" data-testid="restaurant-book-btn">
                    Reserve · 10% deposit
                  </Button>
                );
              }
              // a_la_carte → no Reserve, two budget-based CTAs
              if (model === "a_la_carte") {
                return (
                  <>
                    <Button onClick={() => bookNow("curated")} disabled={loading || !slot} className="flex-1 tablo-btn-outline text-sm" data-testid="restaurant-build-curated-btn">
                      <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> Tablo Curate
                    </Button>
                    <Button onClick={() => bookNow("pick")} disabled={loading || !slot} className="flex-1 tablo-btn-primary text-sm" data-testid="restaurant-design-menu-btn">
                      <ChefHat className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> Design my menu
                    </Button>
                  </>
                );
              }
              // hybrid → CTAs switch by active tab
              if (activeTab === "packages") {
                return (
                  <Button onClick={() => bookNow("package")} disabled={loading || !slot || !pkg} className="flex-1 tablo-btn-primary text-sm" data-testid="restaurant-book-btn">
                    Reserve package · 10% deposit
                  </Button>
                );
              }
              // hybrid + menu tab → budget-based CTAs
              return (
                <>
                  <Button onClick={() => bookNow("curated")} disabled={loading || !slot} className="flex-1 tablo-btn-outline text-sm" data-testid="restaurant-build-curated-btn">
                    <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> Tablo Curate
                  </Button>
                  <Button onClick={() => bookNow("pick")} disabled={loading || !slot} className="flex-1 tablo-btn-primary text-sm" data-testid="restaurant-design-menu-btn">
                    <ChefHat className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> Design my menu
                  </Button>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
